import { firstTag } from "./tags.ts";
import type { OsmTags } from "./types.ts";

interface OsmContactTags {
  website?: string;
  phone?: string;
  wikipedia?: string;
  facebook?: string;
  instagram?: string;
}

export interface ContactLink {
  href: string;
  label: string;
}

function contactTags(tags: OsmTags): OsmContactTags {
  return {
    website: firstTag(tags, ["website", "contact:website", "brand:website"]),
    phone: firstTag(tags, ["phone", "contact:phone"]),
    wikipedia: tags["wikipedia"],
    facebook: firstTag(tags, ["contact:facebook", "facebook"]),
    instagram: tags["contact:instagram"],
  };
}

export function contactLinks(tags: OsmTags): {
  websiteLink?: ContactLink;
  phoneLink?: ContactLink;
  wikipediaLink?: ContactLink;
  facebookLink?: ContactLink;
  instagramLink?: ContactLink;
} {
  const contact = contactTags(tags);
  return {
    websiteLink: contact.website ? websiteLink(contact.website) : undefined,
    phoneLink: contact.phone ? phoneLink(contact.phone) : undefined,
    wikipediaLink: contact.wikipedia
      ? wikipediaLink(contact.wikipedia)
      : undefined,
    facebookLink: contact.facebook ? facebookLink(contact.facebook) : undefined,
    instagramLink: contact.instagram
      ? instagramLink(contact.instagram)
      : undefined,
  };
}

// Convert bare website tags into clickable HTTPS URLs while preserving the
// schemes supported by the contact UI.
function websiteHref(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const trimmed = value.trim();
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return `https://${trimmed}`;

  try {
    const protocol = new URL(trimmed).protocol.toLowerCase();
    if (
      protocol !== "http:" && protocol !== "https:"
    ) {
      return undefined;
    }
  } catch {
    return undefined;
  }

  return trimmed;
}

function websiteLink(value: string): ContactLink | undefined {
  const href = websiteHref(value);
  return href ? { href, label: websiteLabel(href) } : undefined;
}

function facebookLink(value: string): ContactLink | undefined {
  return socialProfileLink(value, "https://www.facebook.com/");
}

function instagramLink(value: string): ContactLink | undefined {
  return socialProfileLink(value, "https://www.instagram.com/");
}

function wikipediaLink(value: string): ContactLink | undefined {
  if (!value.trim()) return undefined;

  const trimmed = value.trim();
  const explicitLink = explicitWikipediaLink(trimmed);
  if (explicitLink) return explicitLink;

  // OSM wikipedia=* values are language-prefixed article titles, for example
  // "en:St Paul's Cathedral".
  // https://wiki.openstreetmap.org/wiki/Key:wikipedia
  const match = /^([a-z][a-z0-9-]*)\s*:\s*(.+)$/i.exec(trimmed);
  if (!match) return undefined;

  const language = match[1];
  const title = match[2];
  if (!language || !title) return undefined;

  const trimmedTitle = title.trim();
  return trimmedTitle
    ? {
      href:
        `https://${language.toLowerCase()}.wikipedia.org/wiki/${trimmedTitle}`,
      label: trimmedTitle,
    }
    : undefined;
}

function phoneHref(value: string): string | undefined {
  if (!value.trim()) return undefined;

  // OSM maps multiple phone numbers as semicolon-separated values:
  // https://wiki.openstreetmap.org/wiki/Key:phone#How_to_map
  const firstNumber = value
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.length > 0);

  if (!firstNumber) return undefined;

  // Dial links omit visual separators, and avoid known non-number mistakes:
  // https://wiki.openstreetmap.org/wiki/Key:phone#Parsing_phone_numbers
  // https://wiki.openstreetmap.org/wiki/Key:phone#Possible_tagging_mistakes
  const dialable = firstNumber.replace(/[\s().-]/g, "");
  return /^\+?\d+$/.test(dialable) ? `tel:${dialable}` : undefined;
}

function phoneLink(value: string): ContactLink | undefined {
  const href = phoneHref(value);
  return href && value.trim() ? { href, label: value.trim() } : undefined;
}

function websiteLabel(website: string): string {
  return website
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");
}

function explicitWikipediaLink(value: string): ContactLink | undefined {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return undefined;
  }

  if (!/^https?:$/.test(url.protocol)) return undefined;
  if (!/(^|\.)wikipedia\.org$/i.test(url.hostname)) return undefined;

  return {
    href: value,
    label: value,
  };
}

function socialProfileLink(
  value: string,
  baseHref: string,
): ContactLink | undefined {
  if (!value.trim()) return undefined;

  const trimmed = value.trim();
  if (/^(?:yes|no|none|null)$/i.test(trimmed)) return undefined;

  if (/^https?:\/\//i.test(trimmed)) {
    return {
      href: trimmed,
      label: trimmed,
    };
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return undefined;

  const accountName = socialAccountName(trimmed);
  if (!accountName) return undefined;

  return {
    href: `${baseHref}${encodeURIComponent(accountName)}`,
    label: accountName,
  };
}

function socialAccountName(value: string): string | undefined {
  const accountName = value.trim().replace(/^@+/, "").replace(/^\/+|\/+$/g, "");
  if (!accountName) return undefined;
  if (/[\s/?#]/.test(accountName)) return undefined;
  return accountName;
}
